import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Button,
    Chip
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    StarIcon
} from "../../anatomy/icons";

const NAME = "Chip";

const USAGE_CODE = `const [isLive, setIsLive] = useState(false);

<Chip
    onPress={() => setIsLive(!isLive)}
    isSelected={isLive}
    title="Live"
/>

<Chip
    onClose={() => removeFilter("components")}
    title="Components"
    type="neutral"
    size="small"
/>`;

const ICON_CODE = `<Chip
    accessibilityLabel="Favourite"
    icon={StarIcon}
/>`;

const TOPICS = [
    {
        key: "chipPage.topicDesign",
        id: "design"
    },
    {
        key: "chipPage.topicEngineering",
        id: "engineering"
    },
    {
        key: "chipPage.topicResearch",
        id: "research"
    }
] as const;

const VARIANTS = [
    "tonal",
    "outline"
] as const;

const TYPES = [
    "primary",
    "neutral",
    "danger",
    "warning",
    "success",
    "info"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const INITIAL_SELECTED = [
    "design"
];

const LOADING_DELAY = 1200;

const ROW_STYLE = {
    justifyContent: "center",
    flexWrap: "wrap",
    display: "flex",
    gap: 12
} as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("chipPage.appearanceTitle"),
        id: "appearance"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("chipPage.iconTitle"),
        id: "icon"
    },
    {
        title: t("chipPage.removableTitle"),
        id: "removable"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ChipPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        selected,
        setSelected
    ] = useState(INITIAL_SELECTED);

    const [
        isStarred,
        setIsStarred
    ] = useState(false);

    const [
        tags,
        setTags
    ] = useState<Array<string>>(TOPICS.map((topic) => topic.id));

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    useEffect(() => {
        if(!isLoading) {
            return;
        }

        const timer = window.setTimeout(() => setIsLoading(false), LOADING_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoading
    ]);

    const handleToggle = (id: string) => {
        setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [
            ...current,
            id
        ]);
    };

    const handleRemove = (id: string) => {
        setTags((current) => current.filter((item) => item !== id));
    };

    return <Article>
        <ComponentHeader
            lead={t("chipPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("chipPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("chipPage.overviewCaption")}
            >
                {
                    TOPICS.map((topic) => <Chip
                        isSelected={selected.includes(topic.id)}
                        onPress={() => handleToggle(topic.id)}
                        spreadBehaviour="free"
                        title={t(topic.key)}
                        key={topic.id}
                    />)
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("chipPage.appearanceTitle")}
            id="appearance"
        >
            <Paragraph>
                {t("chipPage.appearanceText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    VARIANTS.map((variant) => <div
                        style={ROW_STYLE}
                        key={variant}
                    >
                        {
                            TYPES.map((type) => <Chip
                                variant={variant}
                                title={type}
                                type={type}
                                key={type}
                            />)
                        }
                    </div>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("chipPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <Chip
                        icon={StarIcon}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("chipPage.iconTitle")}
            id="icon"
        >
            <Paragraph>
                {t("chipPage.iconText")}
            </Paragraph>
            <Preview
                caption={t("chipPage.iconCaption")}
            >
                <Chip
                    title={t("chipPage.favourite")}
                    spreadBehaviour="free"
                    icon={StarIcon}
                />
                <Chip
                    accessibilityLabel={t("chipPage.favourite")}
                    spreadBehaviour="free"
                    icon={StarIcon}
                />
                <Chip
                    onPress={() => setIsStarred(!isStarred)}
                    title={t("chipPage.favourite")}
                    spreadBehaviour="free"
                    isSelected={isStarred}
                    icon={StarIcon}
                />
            </Preview>
            <CodeBlock
                code={ICON_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("chipPage.removableTitle")}
            id="removable"
        >
            <Paragraph>
                {t("chipPage.removableText")}
            </Paragraph>
            <Preview
                caption={t("chipPage.removableCaption")}
            >
                {
                    TOPICS.filter((topic) => tags.includes(topic.id)).map((topic) => <Chip
                        onClose={() => handleRemove(topic.id)}
                        spreadBehaviour="free"
                        title={t(topic.key)}
                        key={topic.id}
                    />)
                }
                <Button
                    onPress={() => setTags(TOPICS.map((topic) => topic.id))}
                    title={t("chipPage.reset")}
                    spreadBehaviour="free"
                    variant="ghost"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("chipPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("chipPage.statesCaption")}
            >
                <Chip
                    title={t("chipPage.disabled")}
                    onPress={() => undefined}
                    spreadBehaviour="free"
                    isDisabled={true}
                />
                <Chip
                    onPress={() => setIsLoading(true)}
                    title={t("chipPage.loading")}
                    spreadBehaviour="free"
                    isLoading={isLoading}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("chipPage.a11yRoles"),
                    t("chipPage.a11yToggle"),
                    t("chipPage.a11yClose"),
                    t("chipPage.a11yKeyboard"),
                    t("chipPage.a11yStates"),
                    t("chipPage.a11yHitArea"),
                    t("chipPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("chipPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("chipPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("chipPage.propVariant"),
                        type: "\"tonal\" | \"outline\"",
                        defaultValue: "\"tonal\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("chipPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("chipPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("chipPage.propIsSelected"),
                        name: "isSelected",
                        type: "boolean"
                    },
                    {
                        description: t("chipPage.propIsClosable"),
                        defaultValue: "!!onClose",
                        name: "isClosable",
                        type: "boolean"
                    },
                    {
                        description: t("chipPage.propOnClose"),
                        type: "() => void",
                        name: "onClose"
                    },
                    {
                        description: t("chipPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("chipPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("chipPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("chipPage.propIsPressScaleEnabled"),
                        name: "isPressScaleEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("chipPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        type: "keyof ContainerContentColors | keyof ProjectColorPalette | string",
                        description: t("chipPage.propCustomColor"),
                        name: "customColor"
                    },
                    {
                        type: "keyof TextContentColors | keyof ProjectColorPalette | string",
                        description: t("chipPage.propCustomTitleColor"),
                        name: "customTitleColor"
                    },
                    {
                        type: "keyof IconContentColors | keyof ProjectColorPalette | string",
                        description: t("chipPage.propCustomIconColor"),
                        name: "customIconColor"
                    },
                    {
                        description: t("chipPage.propTitleStyle"),
                        type: "StyleProp<TextStyle>",
                        name: "titleStyle"
                    },
                    {
                        description: t("chipPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("chipPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("chipPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("chipPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ChipPage;
